import React from 'react';
import { Link } from 'react-router-dom';
import { Feather, Recycle, Zap, FlaskConical, Globe2, AlertTriangle, ArrowRight, Flame, Thermometer, Layers, Building2, Route, Sprout, Factory, Check } from 'lucide-react';
import Reveal from '../components/Reveal';
import { images, magnesiumFacts, geopolitics, applications, mgo, downstream } from '../mock';
import PageHeader from '../components/PageHeader';
import atrIngots from '../assets/atr-ingots.jpg';
import bigBlueLogo from '../assets/bigbluetech.png';

const factIcons = [Feather, Zap, Feather, FlaskConical, FlaskConical, Recycle];
const mgoPropIcons = [Flame, Thermometer, Layers, Building2, Route];
const mgoAppIcons = [Flame, Building2, Sprout, Route];

const Magnesium = () => {
  return (
    <div>
      <PageHeader
        eyebrow="The Metal"
        title="Magnesium"
        subtitle="The lightest structural metal, and one of the most strategically important critical minerals of the modern era."
        image={images.alloyBars}
      />

      {/* KEY FACTS */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow text-amber-700 mb-4">Properties</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 max-w-2xl">
              Light, strong, and endlessly recyclable.
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
            {magnesiumFacts.map((f, i) => {
              const Icon = factIcons[i % factIcons.length];
              return (
                <Reveal key={i} delay={(i % 3) * 90}>
                  <div className="border-t border-neutral-200 pt-6">
                    <Icon className="w-6 h-6 text-amber-700 mb-4" strokeWidth={1.5} />
                    <p className="text-neutral-700 leading-relaxed">{f}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPARISON STRIP */}
      <section className="bg-neutral-950 text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-12 text-center">
          <Reveal>
            <div className="font-display text-6xl font-bold">33%</div>
            <div className="mt-3 text-neutral-400">Lighter than aluminum</div>
          </Reveal>
          <Reveal delay={100}>
            <div className="font-display text-6xl font-bold">75%</div>
            <div className="mt-3 text-neutral-400">Lighter than steel</div>
          </Reveal>
          <Reveal delay={200}>
            <div className="font-display text-6xl font-bold">5%</div>
            <div className="mt-3 text-neutral-400">Energy to recycle vs. primary</div>
          </Reveal>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="py-28 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow text-amber-700 mb-4">Applications</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900">Where magnesium matters.</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((a, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="group relative overflow-hidden bg-white h-[360px]">
                  <img src={images[a.img]} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="font-display text-xl font-semibold">{a.title}</h3>
                    <p className="text-neutral-300 text-sm mt-2 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-24 transition-all duration-500">{a.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GEOPOLITICS */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <Globe2 className="w-8 h-8 text-amber-700 mb-5" strokeWidth={1.5} />
              <div className="eyebrow text-amber-700 mb-4">Geopolitics</div>
              <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">
                A critical mineral with a fragile supply chain.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-6">
            {geopolitics.map((g, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="flex gap-5 border-l-2 border-amber-700/40 pl-6 py-1">
                  <p className="text-neutral-700 leading-relaxed text-lg">{g}</p>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <div className="mt-8 flex items-start gap-4 bg-amber-50 border border-amber-200 p-6">
                <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-amber-900 leading-relaxed">
                  With effectively zero domestic production, a US-based, high-purity source is a matter of strategic supply security.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MAGNESIUM OXIDE */}
      <section data-testid="mgo-section" className="border-t border-neutral-200">
        <div className="py-28 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="eyebrow text-amber-700 mb-4">{mgo.eyebrow}</div>
              <h2 data-testid="mgo-title" className="font-display text-3xl md:text-5xl font-medium text-neutral-900 max-w-3xl leading-tight">
                {mgo.title}
              </h2>
            </Reveal>
            <Reveal delay={100}>
              <div className="mt-20 eyebrow text-neutral-500 mb-4">Properties</div>
              <h3 className="font-display text-2xl md:text-3xl font-medium text-neutral-900">{mgo.propertiesTitle}</h3>
            </Reveal>
            <div className="mt-14 grid md:grid-cols-2 gap-x-12 gap-y-10">
              {mgo.properties.map((p, i) => {
                const Icon = mgoPropIcons[i % mgoPropIcons.length];
                return (
                  <Reveal key={i} delay={(i % 2) * 90}>
                    <div className="border-t border-neutral-300 pt-6">
                      <Icon className="w-6 h-6 text-amber-700 mb-4" strokeWidth={1.5} />
                      <p className="text-neutral-700 leading-relaxed">{p}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-neutral-950 text-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-12 text-center">
            {mgo.stats.map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div data-testid={`mgo-stat-${i}`} className="font-display text-5xl md:text-6xl font-bold">{s.value}</div>
                <div className="mt-3 text-neutral-400 max-w-xs mx-auto">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="eyebrow text-amber-700 mb-4">Applications</div>
              <h3 className="font-display text-3xl md:text-4xl font-medium text-neutral-900">{mgo.applicationsTitle}</h3>
            </Reveal>
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mgo.applications.map((a, i) => {
                const Icon = mgoAppIcons[i];
                return (
                  <Reveal key={i} delay={i * 90}>
                    <div data-testid={`mgo-app-${i}`} className="group bg-stone-50 border border-neutral-200 p-8 h-full hover:border-amber-700/50 transition-colors duration-500">
                      <Icon className="w-7 h-7 text-amber-700 mb-8" strokeWidth={1.5} />
                      <h4 className="font-display text-xl font-semibold text-neutral-900">{a.title}</h4>
                      <p className="text-neutral-600 text-sm leading-relaxed mt-3">{a.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        <div className="py-28 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-4">
              <Reveal>
                <Factory className="w-8 h-8 text-amber-700 mb-5" strokeWidth={1.5} />
                <div className="eyebrow text-amber-700 mb-4">Supply</div>
                <h3 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">{mgo.supplyTitle}</h3>
              </Reveal>
            </div>
            <div className="lg:col-span-8 space-y-6">
              {mgo.supply.map((s, i) => (
                <Reveal key={i} delay={i * 80}>
                  <div className="flex gap-5 border-l-2 border-amber-700/40 pl-6 py-1">
                    <p className="text-neutral-700 leading-relaxed text-lg">{s}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal>
                <div data-testid="mgo-supply-callout" className="mt-8 flex items-start gap-4 bg-amber-50 border border-amber-200 p-6">
                  <ArrowRight className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-amber-900 leading-relaxed">{mgo.supplyCallout}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* DOMESTIC DOWNSTREAM PROCESSING */}
      <section data-testid="downstream-section" className="bg-white border-t border-neutral-200 py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="eyebrow text-amber-700 mb-4">{downstream.eyebrow}</div>
                <h2 data-testid="downstream-title" className="font-display text-3xl md:text-5xl font-medium text-neutral-900 leading-tight max-w-2xl">
                  {downstream.title}
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="mt-10 text-neutral-900 text-lg md:text-xl leading-relaxed font-light border-l-2 border-amber-700 pl-6">
                  {downstream.lead}
                </p>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-8 text-neutral-600 leading-relaxed max-w-2xl">{downstream.body}</p>
              </Reveal>
              <Reveal delay={220}>
                <div className="mt-12 border-t border-neutral-200 pt-8">
                  <div className="eyebrow text-neutral-500 mb-6">{downstream.advantagesTitle}</div>
                  <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                    {downstream.advantages.map((a, i) => (
                      <li key={i} className="flex items-start gap-3 text-neutral-800">
                        <Check className="w-4 h-4 text-amber-700 mt-1 shrink-0" strokeWidth={2} />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={280}>
                <div className="mt-12 flex items-center gap-6 border-t border-neutral-200 pt-8">
                  <img src={bigBlueLogo} alt="Big Blue Technologies" className="h-9 w-auto object-contain" />
                  <span className="text-neutral-500 text-sm">{downstream.partnerNote}</span>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={140}>
                <div className="overflow-hidden bg-neutral-100">
                  <img
                    src={atrIngots}
                    alt="Magnesium metal ingots produced from Whitemoon ore"
                    className="w-full h-auto hover:scale-[1.03] transition-transform duration-1000"
                  />
                </div>
                <p className="mt-4 text-neutral-500 text-sm leading-relaxed">{downstream.photoCaption}</p>
              </Reveal>
            </div>
          </div>

          {/* ATR FLOW */}
          <Reveal delay={120}>
            <div className="mt-20 bg-neutral-950 text-white p-10 md:p-14">
              <div className="eyebrow text-amber-500 mb-10">{downstream.flowTitle}</div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-6">
                {downstream.flow.map((f, i) => (
                  <React.Fragment key={i}>
                    <div
                      data-testid={`atr-node-${i}`}
                      className="w-24 h-28 md:w-28 md:h-32 flex items-center justify-center bg-white/5 border border-white/15"
                      style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
                    >
                      <span className="font-display text-xl md:text-2xl font-bold tracking-tight">{f.label}</span>
                    </div>
                    {f.op && (
                      <span className="font-display text-2xl md:text-3xl text-amber-500">{f.op}</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-10 text-neutral-400 text-sm max-w-2xl leading-relaxed">
                Source: Big Blue Technologies. Byproduct varies with feed - magnesium aluminate spinel from MgO, calcium aluminate from MgO-CaO.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-neutral-950 py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-white">Part of the solution.</h2>
            <Link to="/project" className="mt-8 inline-flex items-center gap-2 bg-amber-500 text-white px-9 py-4 eyebrow hover:bg-amber-600 transition-colors duration-300">
              See the project <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Magnesium;
